import type { Vector3 } from 'three';
import type { PlayerController } from '../movement/PlayerController';
import { type ZoneDef, zoneContains } from './zones';

/**
 * Map effect triggers — speed-boost pads and teleport portals — advanced once
 * per simulation tick after the player moves. Effects fire on the RISING edge
 * (the tick the player enters a zone) so a boost is applied once per pass, not
 * every tick the capsule overlaps the pad.
 *
 * Design inspiration: SurfUnity's teleport portals and BevyHop's boost pad (both
 * MIT/Apache); geometry and code here are original to Fall Line.
 */
export class MapTriggers {
  private readonly boosts: ZoneDef[];
  private readonly teleports: ZoneDef[];
  /** Zones the player occupied last tick — for rising-edge detection. */
  private readonly occupied = new Set<ZoneDef>();

  constructor(zones: ZoneDef[]) {
    this.boosts = zones.filter((z) => z.kind === 'boost');
    this.teleports = zones.filter((z) => z.kind === 'teleport');
  }

  /** True when this map has any effect triggers (skip the per-tick work otherwise). */
  get active(): boolean {
    return this.boosts.length > 0 || this.teleports.length > 0;
  }

  /** Call once per simulation tick, after player.tick(). */
  tick(player: PlayerController): void {
    for (const zone of this.boosts) {
      if (this.entered(zone, player.position)) {
        player.boostLateral(zone.boost ?? 1);
      }
    }
    for (const zone of this.teleports) {
      if (this.entered(zone, player.position) && zone.dest) {
        player.warp(zone.dest);
      }
    }
  }

  /** Rising-edge test: true only on the first tick the point is inside `zone`. */
  private entered(zone: ZoneDef, point: Vector3): boolean {
    if (zoneContains(zone, point)) {
      if (this.occupied.has(zone)) {
        return false;
      }
      this.occupied.add(zone);
      return true;
    }
    this.occupied.delete(zone);
    return false;
  }

  reset(): void {
    this.occupied.clear();
  }
}
